import { z } from 'zod';

const stepSchema = z.enum([
  'initiated',
  'cancel',
  'success',
  'add_recommended_service',
  'choose_time',
  'search_time',
  'removed_service',
  'service_selected',
  'employee_selected',
  'back_to_addon_selection',
  'back_to_employee_selection',
  'back_to_service_selection',
  'back_to_choose_time',
  'booking_cta',
  'closed',
  'checkout_confirmation',
  'validation_error',
  'error',
  'back_to_checkout_summary',
]);

export type Step = z.infer<typeof stepSchema>;

const stepCategorySchema = z.enum([
  'selection',
  'summary',
  'add_service',
  'T&C_merchant',
  'T&C_bd',
  'checkout_confirmation',
  'checkout_payment_methods',
  'view_service_details_unbookable',
  'view_service_details_bookable',
]);

const paymentMethodSchema = z.enum(['klarna', 'qliro', 'walley', 'apple_pay', 'google_pay', 'swish', 'card']);

/**
 * InitiatedFrom should ideally be a screen name schema but its
 * defined as string to not break validation since its still some not typed stuff
 * using it.
 */
export const initiatedFromSchema = z.string();

export type InitiatedFrom = z.infer<typeof initiatedFromSchema>;

const companySubscriptionTypeSchema = z.string(); // z.enum(['stable', 'growth', 'essential']);

export const bookingTypeSchema = z.enum(['marketplace', 'sistaminuten', 'deal']);

export const paymentMethodsSchema = z.enum([
  'pay_in_salon',
  'swish',
  'free',
  'qliro',
  'klarna',
  'walley',
  'apple_pay',
  'card',
  'google_pay',
  'gift_card',
  'wellness_card',
  'value_card',
  'bundle',
]);

export const selectedPaymentMethodSchema = z.array(paymentMethodsSchema);

const availablePaymentMethodsSchema = z.array(paymentMethodsSchema.or(z.literal('add_new_card')));

const preSelectedPaymentMethodSchema = z.array(paymentMethodsSchema);

export const monetizationSourceSchema = z.array(z.enum(['SMT', 'Raketen', 'deals', 'payment_solution']));

const errorTypeSchema = z.enum([
  'time_slot_not_available',
  'card_refused',
  'apple_pay_error',
  'google_pay_error',
  'swish_error',
  'klarna_error',
  'qliro_error',
  'summary_error',
  'other',
]);

export type ErrorType = z.infer<typeof errorTypeSchema>;

const validationErrorTypeSchema = z.enum([
  'merchant_terms',
  'payment_methods',
  'form_fields',
  'missing_participants',
  'email_missing',
]);

export type ValidationErrorType = z.infer<typeof validationErrorTypeSchema>;

export const sessionIdSchema = z.string().uuid();

export const bookingFlowPropertiesSchema = z.object({
  booking_session_id: sessionIdSchema.optional(),
  step: stepSchema.optional(),
  step_category: stepCategorySchema.optional(),
  booking_id: z.number().optional(),
  company_id: z.number().optional(),
  company_subscription_type: companySubscriptionTypeSchema.optional(),
  service_ids: z.array(z.string()).optional(),
  employee_selection: z.enum(['any', 'specific', 'none']).optional(),
  initiated_from: initiatedFromSchema,
  booking_type: bookingTypeSchema.optional(),
  /**
   * the amount of time slots the user had "seen" in the time picker upon selecting
   */
  timeslots_available: z.number().optional(),
  /**
   * Properties below is only used from the summary step and onward in the tracking flow
   */
  days_until_booking: z.string().optional(),
  /**
   * list of available payment methods – klippkort will be synthetically added
   */
  available_payment_methods: availablePaymentMethodsSchema.optional(),
  /**
   * usually one, except for when klippkort is being used with an additional service
   */
  preselected_payment_method: preSelectedPaymentMethodSchema.optional(),
  /**
   * can be 1-x items (payment method + klippkort + gift card flavors)
   */
  selected_payment_method: selectedPaymentMethodSchema.optional(),
  /**
   * number of required form fields to fill out
   */
  number_of_required_fields: z.number().optional(),
  /**
   * total price
   */
  gmv: z.number().optional(),
  /**
   * final price
   */
  paid_price: z.number().optional(),
  has_campaigns: z.boolean().optional(),
  has_previous_bookings_at_company: z.boolean().optional(),
  is_bokadirekt_pay: z.boolean().optional(),
  platform_fee: z.boolean().optional(),
  /**
   * anything that is not pay in salon, pay in salon can be monitized if Raketen
   */
  monetization_source: monetizationSourceSchema.optional(),

  /**
   * short lived properties only for specific steps
   */
  payment_method: paymentMethodSchema.optional(),
  error_type: z.array(errorTypeSchema.or(validationErrorTypeSchema)).optional(),
  error_message: z.string().optional(),
});

export type BookingFlowProperties = z.infer<typeof bookingFlowPropertiesSchema>;

export const bookingFlowSchema = z.object({
  name: z.literal('booking_flow'),
  properties: bookingFlowPropertiesSchema,
});
